import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  LessonsAttendanceVisual,
  LessonsScheduleVisual,
  ProductFamilyVisual,
  StaffKioskVisual,
  StaffScheduleVisual,
} from "../components/barnrelay/BarnRelayProductVisuals";
import "./BarnRelay.css";

const PAGE_TITLE = "BarnRelay | Operations Software for Riding Schools | Josh Tillo";
const PAGE_DESCRIPTION =
  "BarnRelay is operations software for riding schools, with focused tools for lesson schedules, attendance, make-up credits, helper shifts, and staff coordination.";

const DEMO_EMAIL = "joshtillo@gmail.com";
const DEMO_SUBJECT = "BarnRelay Demo Request";
const DEMO_BODY = `Hi Josh,

I'm interested in learning more about BarnRelay.

Riding school:

I'm interested in: Lessons / Staff / Both / Not sure yet

Anything you'd like to know about our current setup:

Thanks!`;

const DEMO_HREF = `mailto:${DEMO_EMAIL}?subject=${encodeURIComponent(
  DEMO_SUBJECT
)}&body=${encodeURIComponent(DEMO_BODY)}`;

const navItems = [
  { href: "#lessons", label: "Lessons" },
  { href: "#staff", label: "Staff & Kiosk" },
  { href: "#our-story", label: "Our Story" },
];

function BarnRelayHeader() {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };

    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <header className="br-header">
      <div className="br-header-inner">
        <a className="br-wordmark" href="#barnrelay-top" onClick={closeMenu}>
          BarnRelay<span aria-hidden="true">.</span>
        </a>
        <nav className="br-desktop-nav" aria-label="BarnRelay page">
          {navItems.map((item) => (
            <a href={item.href} key={item.href}>{item.label}</a>
          ))}
        </nav>
        <a className="br-button br-button-primary br-header-cta" href="#request-demo">
          Request a demo
        </a>
        <button
          className="br-menu-button"
          type="button"
          aria-expanded={menuOpen}
          aria-controls="barnrelay-mobile-menu"
          aria-label={menuOpen ? "Close BarnRelay navigation" : "Open BarnRelay navigation"}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span aria-hidden="true" />
          <span aria-hidden="true" />
          <span aria-hidden="true" />
        </button>
      </div>
      <nav
        id="barnrelay-mobile-menu"
        className="br-mobile-nav"
        aria-label="BarnRelay mobile navigation"
        hidden={!menuOpen}
      >
        {navItems.map((item) => (
          <a href={item.href} key={item.href} onClick={closeMenu}>{item.label}</a>
        ))}
        <a className="br-button br-button-primary" href="#request-demo" onClick={closeMenu}>
          Request a demo
        </a>
      </nav>
    </header>
  );
}

function SectionIntro({ eyebrow, title, titleId, children, align = "left" }) {
  return (
    <div className={`br-section-intro br-section-intro-${align}`}>
      <span className="br-eyebrow">{eyebrow}</span>
      <h2 id={titleId}>{title}</h2>
      {children}
    </div>
  );
}

function BarnRelayHero() {
  return (
    <section className="br-hero" aria-labelledby="barnrelay-hero-title">
      <div className="br-shell br-hero-grid">
        <div className="br-hero-copy">
          <span className="br-eyebrow">Operations software for riding schools</span>
          <h1 id="barnrelay-hero-title">
            Your riding school runs on <em>more</em> than a schedule.
          </h1>
          <p>
            BarnRelay provides focused tools for student rosters, recurring lessons,
            attendance, make-up credits and staff operations — built around the
            day-to-day realities of running a riding school.
          </p>
          <div className="br-hero-actions">
            <a className="br-button br-button-primary" href="#request-demo">Request a demo</a>
          </div>
        </div>
        <ProductFamilyVisual />
      </div>
    </section>
  );
}

function CurrentRealitySection() {
  const realities = [
    {
      number: "01",
      title: "The schedule works — until something changes.",
      copy: "Recurring lessons, student assignments and attendance all create information that needs to stay current as the riding school operates.",
    },
    {
      number: "02",
      title: "One missed lesson creates a trail to follow.",
      copy: "Who missed? Are they owed a make-up? Has it been scheduled? Was the credit eventually used? Tracking that lifecycle manually adds up quickly.",
    },
    {
      number: "03",
      title: "The operation extends beyond lessons.",
      copy: "Helpers need to know where they're needed, choose the shifts that work for them and keep track of what they've committed to.",
    },
  ];

  return (
    <section id="current-reality" className="br-section br-reality" aria-labelledby="current-reality-title">
      <div className="br-shell">
        <SectionIntro eyebrow="The current reality" title="Running a riding school takes enough coordination already." titleId="current-reality-title">
          <p>
            When lesson schedules, student information, make-ups and staff coordination
            live across spreadsheets, calendars, email and memory, keeping everything
            aligned becomes another job of its own.
          </p>
        </SectionIntro>
        <div className="br-reality-layout">
          <div className="br-work-materials" aria-hidden="true">
            <div className="br-spreadsheet-paper">
              <div className="br-sheet-row br-sheet-columns">
                <span />
                <span>A</span>
                <span>B</span>
                <span>C</span>
                <span>D</span>
              </div>
              {[
                ["Lesson", "Time", "Student", "Status"],
                ["Beginner", "4:30", "Maya", "Expected"],
                ["Intermediate", "5:30", "Rowan", "Make-up"],
                ["Beginner", "6:30", "Ellis", "Confirmed"],
                ["Saturday", "10:00", "Jamie", "Opening"],
              ].map((row, rowIndex) => (
                <div className="br-sheet-row" key={row.join("-")}>
                  <span className="br-sheet-row-number">{rowIndex + 1}</span>
                  {row.map((cell, cellIndex) => (
                    <span
                      className={rowIndex > 0 && (cellIndex === 1 || cell === "Make-up") ? "is-highlighted" : undefined}
                      key={cell}
                    >
                      {cell}
                    </span>
                  ))}
                </div>
              ))}
            </div>
            <div className="br-ruled-paper">
              <strong>Thursday 4:30</strong>
              <span>Maya — make-up?</span>
              <span>✓ check opening</span>
              <span>□ email parent</span>
              <span>□ confirm instructor</span>
            </div>
            <div className="br-paper-sticky">
              <span>Follow up</span>
              <strong>Check Thursday opening</strong>
            </div>
          </div>
          <ol className="br-reality-list">
            {realities.map((item) => (
              <li key={item.number}>
                <span>{item.number}</span>
                <div><h3>{item.title}</h3><p>{item.copy}</p></div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

function OriginStorySection() {
  return (
    <section id="our-story" className="br-section br-origin" aria-labelledby="origin-title">
      <div className="br-shell br-origin-grid">
        <div className="br-origin-heading">
          <span className="br-eyebrow">Built from a real riding school</span>
          <h2 id="origin-title">BarnRelay started with make-up lessons.</h2>
        </div>
        <div className="br-origin-copy">
          <p>BarnRelay wasn't conceived as a generic scheduling platform. It started with a real problem at the riding school my son attends.</p>
          <p>Their administrator was managing make-up lessons alongside the rest of a busy lesson program using spreadsheets, email, a physical calendar and a lot of knowledge she carried herself.</p>
          <p>I built the first version of BarnRelay to make that one part of her job easier.</p>
          <p>As it became part of the school's day-to-day operation, the opportunity became clearer: student records, lesson schedules, attendance and make-up credits weren't separate problems. They were connected parts of running the lesson program.</p>
          <p className="br-origin-closing">BarnRelay has grown around real operational needs ever since.</p>
        </div>
        <div className="br-origin-path" aria-label="The operational path from make-up tracking to connected lesson operations">
          <span>Make-up lessons</span><i aria-hidden="true" />
          <span>Attendance</span><i aria-hidden="true" />
          <span>Lesson schedules</span><i aria-hidden="true" />
          <span>Student records</span>
        </div>
      </div>
    </section>
  );
}

const lessonsLifecycle = [
  ["Student is scheduled", "A student belongs to a recurring lesson, with their information and parent contacts already connected."],
  ["Attendance is recorded", "Record what actually happened when the lesson takes place."],
  ["Make-up credit is created", "When an eligible absence generates a make-up, the credit becomes part of the student's record rather than another item to remember elsewhere."],
  ["Make-up is scheduled", "Use that credit when the student is placed into an appropriate make-up lesson."],
  ["Credit is consumed", "The completed make-up closes the loop on the credit that made it possible."],
];

function Lifecycle({ items, className = "" }) {
  return (
    <ol className={`br-lifecycle ${className}`.trim()}>
      {items.map(([title, copy], index) => (
        <li key={title}>
          <span className="br-lifecycle-number">{String(index + 1).padStart(2, "0")}</span>
          <div><h3>{title}</h3><p>{copy}</p></div>
        </li>
      ))}
    </ol>
  );
}

function LessonsSection() {
  return (
    <section id="lessons" className="br-section br-lessons" aria-labelledby="lessons-title">
      <div className="br-shell">
        <SectionIntro eyebrow="BarnRelay Lessons" title="From the weekly schedule to the make-up lesson." titleId="lessons-title">
          <p>BarnRelay Lessons brings student records, parent contacts, recurring lesson schedules, attendance and make-up credits into one focused application — so the information needed to manage the lesson program stays with the work.</p>
        </SectionIntro>
        <div className="br-lessons-lead">
          <Lifecycle items={lessonsLifecycle} />
          <LessonsAttendanceVisual />
        </div>
        <div className="br-lessons-support">
          <LessonsScheduleVisual />
          <div className="br-capability-list">
            <div><h3>Students & Families</h3><p>Keep student records and related parent contacts together.</p></div>
            <div><h3>Lesson Schedule</h3><p>Manage recurring lessons and the students assigned to them.</p></div>
            <div><h3>Attendance</h3><p>Record attendance against the lesson actually taking place.</p></div>
            <div><h3>Make-up Credits</h3><p>Create, schedule and consume make-up credits without maintaining a separate tracker.</p></div>
          </div>
        </div>
      </div>
    </section>
  );
}

function LessonsToStaffTransition() {
  return (
    <aside className="br-transition" aria-labelledby="transition-title">
      <div className="br-shell">
        <span className="br-transition-line" aria-hidden="true" />
        <h2 id="transition-title">Lessons solved one side of the operation.</h2>
        <p>As BarnRelay became part of the school's day-to-day workflow, another need emerged: coordinating the helpers who keep things moving around the barn.</p>
      </div>
    </aside>
  );
}

const staffLifecycle = [
  ["Shifts needed", "The barn identifies where helpers are needed."],
  ["Available", "Those needs become shifts helpers can choose from."],
  ["Claimed", "Helpers sign up for shifts that work for them."],
  ["Worked", "Helpers sign in when they arrive for their shifts."],
  ["Recorded", "Administrators retain a record of completed work."],
];

function StaffSection() {
  return (
    <section id="staff" className="br-section br-staff" aria-labelledby="staff-title">
      <div className="br-shell">
        <SectionIntro eyebrow="BarnRelay Staff" title="Show what's needed. Let helpers choose their shifts." titleId="staff-title">
          <p>BarnRelay Staff turns the barn's staffing needs into available shifts that helpers can sign up for themselves.</p>
          <p>Helpers choose the shifts that work for them, while administrators retain a clear view of what's covered, what's still needed and who's signed up.</p>
        </SectionIntro>
        <StaffScheduleVisual />
        <Lifecycle items={staffLifecycle} className="br-staff-lifecycle" />
      </div>
    </section>
  );
}

function StaffKioskSection() {
  return (
    <section id="staff-kiosk" className="br-section br-kiosk" aria-labelledby="kiosk-title">
      <div className="br-shell br-kiosk-layout">
        <div>
          <span className="br-eyebrow">BarnRelay Staff Kiosk</span>
          <h2 id="kiosk-title">Everything helpers need for their shifts.</h2>
          <p>Staff Kiosk gives helpers a focused place to see available shifts, sign up for the ones they want and keep track of what's coming up.</p>
          <p>When it's time to work, they can sign in for their shift so BarnRelay has a record of the work that was actually completed.</p>
          <div className="br-kiosk-path" aria-label="Available shifts become upcoming shifts and then worked shifts">
            <span>Available</span><i aria-hidden="true">→</i>
            <span>Upcoming</span><i aria-hidden="true">→</i>
            <span>Worked</span>
          </div>
        </div>
        <StaffKioskVisual />
      </div>
    </section>
  );
}

function BuiltForFocusSection() {
  return (
    <section id="built-for-focus" className="br-section br-focus" aria-labelledby="focus-title">
      <div className="br-shell">
        <SectionIntro eyebrow="Built for focus" title="The right information for the job at hand." titleId="focus-title" align="center">
          <p>BarnRelay brings purpose-built riding-school tools under one product family without putting every part of the operation on the same screen.</p>
        </SectionIntro>
        <div className="br-focus-map">
          <article>
            <span className="br-focus-app">BarnRelay Lessons</span>
            <h3>Focus on the lesson program.</h3>
            <ul><li>Students</li><li>Families</li><li>Recurring lessons</li><li>Attendance</li><li>Make-up credits</li></ul>
            <p className="br-context-note">Relevant Staff coverage can appear with the lesson where it is useful.</p>
          </article>
          <div className="br-focus-divider" aria-hidden="true">
            <span>Relevant context</span>
            <i>↔</i>
          </div>
          <article>
            <span className="br-focus-app">BarnRelay Staff</span>
            <h3>Focus on the helpers who keep the barn moving.</h3>
            <ul><li>Available shifts</li><li>Sign-ups</li><li>Upcoming shifts</li><li>Shift sign-ins</li><li>Completed shifts</li></ul>
            <div className="br-kiosk-nested"><b>Staff Kiosk</b><span>Relevant lesson context comes with the shift.</span></div>
          </article>
        </div>
        <blockquote>More information isn't always better. The right information at the right time is.</blockquote>
        <p className="br-focus-closing">Different needs. Different workflows. Built for the same riding school.</p>
      </div>
    </section>
  );
}

function DemoSection() {
  return (
    <section id="request-demo" className="br-demo" aria-labelledby="demo-title">
      <div className="br-shell br-demo-inner">
        <div>
          <span className="br-eyebrow">Request a demo</span>
          <h2 id="demo-title">See what BarnRelay could look like at your riding school.</h2>
          <p>Interested in BarnRelay? Get in touch and tell me a little about your riding school. I'd be happy to walk you through Lessons, Staff, or both and talk about how they could fit your operation.</p>
        </div>
        <a className="br-button br-button-light" href={DEMO_HREF}>Email me about BarnRelay</a>
      </div>
    </section>
  );
}

function BarnRelayFooter() {
  return (
    <footer className="br-footer">
      <div className="br-shell br-footer-grid">
        <div><a className="br-wordmark" href="#barnrelay-top">BarnRelay<span aria-hidden="true">.</span></a><p>Operations software for riding schools.</p></div>
        <nav aria-label="BarnRelay footer navigation">
          <a href="#lessons">Lessons</a>
          <a href="#staff">Staff & Kiosk</a>
          <a href="#our-story">Our Story</a>
          <a href="#request-demo">Request a demo</a>
        </nav>
        <div className="br-footer-attribution">
          <p>Designed and built by <Link to="/">Josh Tillo</Link>.</p>
          <p>© 2026 Josh Tillo.</p>
        </div>
      </div>
    </footer>
  );
}

export default function BarnRelay() {
  useEffect(() => {
    const previousTitle = document.title;
    let description = document.querySelector('meta[name="description"]');
    const createdDescription = !description;
    if (!description) {
      description = document.createElement("meta");
      description.setAttribute("name", "description");
      document.head.appendChild(description);
    }
    const previousDescription = description?.getAttribute("content") ?? "";

    document.title = PAGE_TITLE;
    if (description) description.setAttribute("content", PAGE_DESCRIPTION);

    return () => {
      document.title = previousTitle;
      if (createdDescription) {
        description.remove();
      } else {
        description.setAttribute("content", previousDescription);
      }
    };
  }, []);

  return (
    <div id="barnrelay-top" className="barnrelay-page">
      <a className="br-skip-link" href="#barnrelay-main">Skip to main content</a>
      <BarnRelayHeader />
      <main id="barnrelay-main">
        <BarnRelayHero />
        <CurrentRealitySection />
        <OriginStorySection />
        <LessonsSection />
        <LessonsToStaffTransition />
        <StaffSection />
        <StaffKioskSection />
        <BuiltForFocusSection />
        <DemoSection />
      </main>
      <BarnRelayFooter />
    </div>
  );
}

export { DEMO_BODY, DEMO_EMAIL, DEMO_HREF, DEMO_SUBJECT, PAGE_DESCRIPTION, PAGE_TITLE };
